import React, { useEffect, useRef, useState } from 'react';
import { IconPhone } from './icons';
import { contextoVoz } from '../engine/agentes';
import { agendarReal } from '../lib/agenda';

const API = process.env.REACT_APP_BACKEND_URL;

// Guion de la voz por CASO (según el momento del lead): carro, sismo, hijo, trabajo, hogar.
// No recomienda, no cotiza, no dictamina reclamos: confirma y transfiere al asesor humano.
function lineaCaso(lead) {
  const txt = `${lead.necesidad || ''} ${(lead.productos || []).join(' ')}`.toLowerCase();
  if (/carro|veh|auto|choqu|estren/.test(txt)) return 'Vi que estrenas carro: revisaremos el todo riesgo y qué te pide el banco, sin letra pequeña.';
  if (/tembl|sismo|terremoto|apart|copropiedad|hogar|casa|mud/.test(txt)) return 'Vi lo del temblor: miraremos qué cubre el seguro del crédito y qué le falta a tus muebles.';
  if (/hij|beb|familia|vida|falt/.test(txt)) return 'Vi que llegó un hijo a la familia: veremos cómo proteger a los que dependen de ti.';
  if (/trabajo|renunci|empleo|independ/.test(txt)) return 'Vi que cambiaste de trabajo: revisaremos qué coberturas conservas y cuáles conviene retomar.';
  return `Vi tu caso: ${lead.necesidad || 'tu consulta'}.`;
}

// Ejemplo de llamada: solo líneas del asistente (sin inventar respuestas del cliente).
function guionSimulado(marca, lead, ctx) {
  const nombre = (lead.nombre || '').split(' ')[0] || '';
  return [
    { src: 'ai', t: `Hola${nombre ? ' ' + nombre : ''}, soy el asistente virtual de ${marca.nombre}.` },
    { src: 'ai', t: lineaCaso(lead) },
    { src: 'ai', t: `Para tu caso miramos ${ctx.producto}: lo que pones tú si pasa algo, según el catálogo, es "${ctx.deducible}". Lo que falta es ${ctx.falta}.` },
    { src: 'ai', t: 'No te recomiendo ni te cotizo nada por aquí: eso lo confirma un asesor humano.' },
    { src: 'ai', t: `Te agendo con un asesor de ${marca.nombre}. Te escribe por WhatsApp con todo lo que ya hablamos. ¡Gracias!` },
  ];
}

export const VozLlamada = ({ marca, lead, onClose }) => {
  const [estado, setEstado] = useState('conectando'); // conectando · en_llamada · finalizada
  const [modo, setModo] = useState(null); // real · simulado
  const [segundos, setSegundos] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const convRef = useRef(null);
  const timers = useRef([]);
  const endedRef = useRef(false);
  const transcriptRef = useRef([]);
  const modoRef = useRef(null);
  const connectAt = useRef(0);
  const gotMsg = useRef(false);

  const push = (src, t) => setTranscript((tr) => {
    const next = [...tr, { src, t, key: `${Date.now()}-${Math.random()}` }];
    transcriptRef.current = next;
    return next;
  });

  const colgar = () => {
    if (endedRef.current) return;
    endedRef.current = true;
    timers.current.forEach(clearTimeout);
    try { if (convRef.current) convRef.current.endSession(); } catch (e) { /* noop */ }
    setEstado('finalizada');
    // El modo simulado no se guarda en el lead ni cambia su estado.
    onClose(modoRef.current === 'simulado' ? [] : transcriptRef.current, modoRef.current);
  };

  useEffect(() => {
    let mounted = true;
    const ctx = contextoVoz(lead);
    let simIniciado = false;

    const simular = () => {
      if (simIniciado) return; // evita duplicar si onError y onDisconnect coinciden
      simIniciado = true;
      setModo('simulado');
      modoRef.current = 'simulado';
      setEstado('en_llamada');
      const lineas = guionSimulado(marca, lead, ctx);
      let acc = 900;
      lineas.forEach((l) => {
        const id = setTimeout(() => mounted && push(l.src, l.t), acc);
        timers.current.push(id);
        acc += 1900 + l.t.length * 18;
      });
      const fin = setTimeout(() => mounted && colgar(), acc + 1200);
      timers.current.push(fin);
    };

    const arrancar = async () => {
      let info;
      const primerNombre = (lead.nombre || '').split(' ')[0] || '';
      // Las 4 variables dinámicas EXACTAS que usa el agente (nunca vacías ni null).
      const dynamicVariables = {
        nombre: primerNombre || 'de nuevo',
        marca: marca.nombre,
        producto: ctx.producto || 'tu seguro',
        resumen: (lead.necesidad && ctx.falta)
          ? `${lead.necesidad}; falta ${ctx.falta}`
          : (lead.necesidad || 'el caso que conversaste en el chat'),
      };
      try {
        const r = await fetch(`${API}/api/voz/signed-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nombre: dynamicVariables.nombre, marca: dynamicVariables.marca,
            producto: dynamicVariables.producto, resumen: dynamicVariables.resumen,
          }),
        });
        info = await r.json();
      } catch (e) {
        info = { mode: 'simulated' };
      }
      if (!mounted) return;
      if (info.mode !== 'real' && info.mode !== 'public') { simular(); return; }
      try {
        const { Conversation } = await import('@elevenlabs/client');
        setModo('real');
        modoRef.current = 'real';
        // Agente privado → URL firmada · agente público → conexión directa por agentId.
        const sessionOpts = info.mode === 'real'
          ? { signedUrl: info.signed_url }
          : { agentId: info.agent_id };
        // No enviamos overrides de prompt ni firstMessage: el agente ya los tiene.
        const conv = await Conversation.startSession({
          ...sessionOpts,
          dynamicVariables,
          // Herramienta de cliente: el agente la llama al cerrar la cita y aquí se crea en Calendar (HumanX)
          clientTools: {
            agendar_cita: async ({ franja = '', canal = 'Llamada', correo = '' } = {}) => {
              const r = await agendarReal({
                nombre: dynamicVariables.nombre, canal, franja, correo, origen: 'voz',
                producto: dynamicVariables.producto, resumen: dynamicVariables.resumen,
              });
              if (mounted) push('ai', r.ok ? `📅 Cita creada en el calendario del asesor: ${r.texto}` : 'No pude crear la cita en el calendario.');
              return r.ok ? `Cita creada para el ${r.texto}.${r.simulada ? '' : ' La invitación ya salió.'}` : 'No pude crear la cita; el asesor la agenda a mano.';
            },
          },
          onConnect: () => { if (!mounted) return; connectAt.current = Date.now(); setEstado('en_llamada'); },
          onDisconnect: () => {
            if (!mounted || endedRef.current) return;
            // Conexión real caída muy rápido y sin hablar → cae a simulado (nunca pantalla en blanco).
            if (!gotMsg.current && Date.now() - connectAt.current < 6000) {
              try { conv.endSession(); } catch (e) { /* noop */ }
              simular();
            } else {
              colgar();
            }
          },
          onError: (msg, ctxErr) => {
            // Registra el motivo exacto del rechazo de ElevenLabs para depurar el agente.
            console.error('[VozLlamada] ElevenLabs error:', msg, ctxErr);
            if (mounted && !endedRef.current) { try { conv.endSession(); } catch (e) {} simular(); }
          },
          onMessage: (m) => {
            if (!mounted) return;
            gotMsg.current = true;
            const src = m.source === 'user' ? 'user' : 'ai';
            const t = m.message || m.text || '';
            if (t) push(src, t);
          },
        });
        convRef.current = conv;
      } catch (e) {
        console.error('[VozLlamada] startSession falló:', e);
        if (mounted) simular();
      }
    };

    arrancar();
    return () => {
      mounted = false;
      timers.current.forEach(clearTimeout);
      try { if (convRef.current) convRef.current.endSession(); } catch (e) { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (estado !== 'en_llamada') return undefined;
    const id = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [estado]);

  const mmss = `${String(Math.floor(segundos / 60)).padStart(2, '0')}:${String(segundos % 60).padStart(2, '0')}`;

  return (
    <div className="voz-overlay" data-testid="voz-overlay">
      <div className="voz-card" style={{ '--marca': marca.color }}>
        <div className="voz-head">
          <span className="voz-mono" style={{ background: marca.color }}>{marca.mono}</span>
          <div>
            <strong data-testid="voz-marca">Asistente de {marca.nombre}</strong>
            <span className="voz-sub">
              {estado === 'conectando' ? 'Conectando…' : estado === 'finalizada' ? 'Llamada finalizada' : 'En llamada'}
              {modo === 'simulado' && estado !== 'conectando' ? ' · Ejemplo de llamada' : ''}
            </span>
          </div>
          <span className="voz-timer" data-testid="voz-timer">{mmss}</span>
        </div>

        <div className={`voz-wave ${estado === 'en_llamada' ? 'on' : ''}`} aria-hidden>
          {Array.from({ length: 9 }).map((_, i) => (
            <span key={i} style={{ animationDelay: `${i * 0.09}s`, background: marca.color }} />
          ))}
        </div>

        <div className="voz-transcript" data-testid="voz-transcript">
          {transcript.length === 0 ? (
            <p className="voz-vacio">La transcripción aparecerá aquí en vivo.</p>
          ) : transcript.map((l) => (
            <p key={l.key} className={`voz-line ${l.src}`}>
              <b>{l.src === 'ai' ? marca.nombre : lead.nombre || 'Cliente'}:</b> {l.t}
            </p>
          ))}
        </div>

        <button type="button" className="voz-colgar" onClick={colgar} data-testid="voz-colgar">
          <IconPhone size={16} /> Colgar
        </button>
      </div>
    </div>
  );
};
